import React, { useEffect, useState } from "react";
import { signUpWithEmail, signInWithGoogle, saveMarketingPreference } from "../firebase/authMethods";
import { Modal, Button, Form } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";

const USERNAME_MIN_LENGTH = 3;
const USERNAME_MAX_LENGTH = 20;

const SignupModal = ({
  showModal,
  closeModal,
  setUser,
  setLoginModalOpen,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [preferenceWarning, setPreferenceWarning] = useState(false);

  const resetValues = () => {
    setEmail("");
    setPassword("");
    setUsername("");
    setError("");
    setEmailError(false);
    setPasswordError(false);
    setCheckingUsername(false);
    setUsernameAvailable(null);
    setVerificationEmail("");
    setMarketingOptIn(false);
    setPreferenceWarning(false);
  };

  const checkUsername = async (value) => {
    try {
      const response = await fetch("/api/check-username", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: value }),
      });
      const data = await response.json();
      return data.exists;
    } catch (requestError) {
      console.error("Error checking username:", requestError);
      return true;
    }
  };

  useEffect(() => {
    const trimmedUsername = username.trim();
    if (
      trimmedUsername.length < USERNAME_MIN_LENGTH
      || trimmedUsername.length > USERNAME_MAX_LENGTH
    ) {
      setUsernameAvailable(null);
      setCheckingUsername(false);
      return undefined;
    }

    setCheckingUsername(true);
    const delay = setTimeout(async () => {
      const exists = await checkUsername(trimmedUsername);
      setUsernameAvailable(!exists);
      setCheckingUsername(false);
    }, 600);

    return () => clearTimeout(delay);
  }, [username]);

  const handleSignup = async (event) => {
    event.preventDefault();
    setError("");
    setEmailError(false);

    const passwordIsValid = password.length >= 6;
    setPasswordError(!passwordIsValid);
    if (!passwordIsValid) return;

    setLoading(true);

    try {
      const result = await signUpWithEmail(email, password, username, marketingOptIn);
      setPreferenceWarning(!result.preferenceSaved);
      setVerificationEmail(email.trim());
    } catch (signupError) {
      if (signupError.code === "auth/email-already-in-use") {
        setEmailError(true);
      } else if (signupError.code === "auth/weak-password") {
        setPasswordError(true);
      } else {
        setError(signupError.message || "Something went wrong. Try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setLoading(true);
    setError("");

    try {
      const currentUser = await signInWithGoogle();
      if (currentUser === "closed") {
        setError("Google signup was closed. Try again.");
      } else if (currentUser === "error") {
        setError("Google signup failed. Try again.");
      } else {
        // App handles required username onboarding for both Google entry points.
        setUser(currentUser);
        closeModal();
        resetValues();
        // Consent failure must not prevent the required username step.
        try { await saveMarketingPreference(currentUser, marketingOptIn, true); }
        catch (preferenceError) { console.error('Unable to save signup marketing preference:', preferenceError.message); }
      }
    } catch (signupError) {
      setError(signupError.message || "Google signup failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      className="modal"
      show={showModal}
      onHide={() => {
        closeModal();
        resetValues();
      }}
      centered
    >
      <Modal.Header closeButton className="bg-lightgreen text-white modal-head">
        <Modal.Title className="fw-bold">Signup</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-lightgreen text-lightgrey">
        {verificationEmail ? (
          <div className="text-center py-3">
            <h5 className="text-white fw-bold">Check your email</h5>
            <p className="mb-2">
              We sent a verification link to <strong>{verificationEmail}</strong>.
            </p>
            <p className="font-12">
              Open the link, then return here and log in. Also check your spam folder.
            </p>
            {preferenceWarning && (
              <p className="font-12 text-orange">
                Your marketing preference could not be saved. You can set it after login in Account Settings.
              </p>
            )}
            <Button
              type="button"
              className="mt-2 w-100 btn btn-prim text-white fw-bold"
              onClick={() => {
                closeModal();
                resetValues();
                setLoginModalOpen(true);
              }}
            >
              Go to Login
            </Button>
          </div>
        ) : (
          <>
        <Form onSubmit={handleSignup}>
          <Form.Group controlId="formUsername">
            <Form.Label className="text-uppercase">Username</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              minLength={USERNAME_MIN_LENGTH}
              maxLength={USERNAME_MAX_LENGTH}
              required
            />
            <div className="d-flex justify-content-end align-items-center gap-2 pt-1 fst-italic font-12 text-end">
              <div className={usernameAvailable === false ? "text-orange fw-bold" : ""}>
                {checkingUsername
                  ? "Checking availability..."
                  : usernameAvailable === null
                    ? ""
                    : usernameAvailable
                      ? "Username is available."
                      : "Username already taken."}
              </div>
              <div className="text-lightgrey">
                {username.length}/{USERNAME_MAX_LENGTH} characters
              </div>
            </div>
          </Form.Group>

          <Form.Group controlId="formEmail">
            <Form.Label className="text-uppercase">Email address</Form.Label>
            <Form.Control
              type="email"
              placeholder="Enter email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <div className="text-end fst-italic pt-1 font-12 text-orange fw-bold">
              {emailError ? "Email already in use." : ""} &nbsp;
            </div>
          </Form.Group>

          <Form.Group controlId="formPassword">
            <Form.Label className="text-uppercase">Password</Form.Label>
            <Form.Control
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(event) => {
                const nextPassword = event.target.value;
                setPassword(nextPassword);
                if (passwordError && nextPassword.length >= 6) {
                  setPasswordError(false);
                }
              }}
              onBlur={() => setPasswordError(password.length < 6)}
              aria-invalid={passwordError}
              aria-describedby={passwordError ? "passwordError" : undefined}
            />
            {passwordError && (
              <div id="passwordError" className="text-end fst-italic pt-1 font-12 text-orange fw-bold" role="alert">
                Password should be at least 6 characters.
              </div>
            )}
          </Form.Group>

          <Form.Check
            id="marketingConsent"
            className="mt-2 font-12 text-white"
            type="checkbox"
            checked={marketingOptIn}
            onChange={(event) => setMarketingOptIn(event.target.checked)}
            label="I would like to receive FantasyBet news, offers and marketing emails. I can unsubscribe at any time."
          />

          {error && <div className="text-end fst-italic pt-2 font-12 text-orange">{error}</div>}

          <Button
            type="submit"
            className="mt-3 w-100 btn btn-prim text-white fw-bold"
            disabled={!usernameAvailable || loading}
          >
            {loading ? <Spinner animation="border" size="sm" /> : "Sign Up"}
          </Button>
        </Form>

        <div className="text-center mt-1">or</div>
        <button
          type="button"
          className="mt-1 w-100 align-items-center d-flex justify-content-center btn border fw-bold text-white p-2"
          onClick={handleGoogleSignup}
          disabled={loading}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-google" viewBox="0 0 16 16">
            <path d="M15.545 6.558a9.4 9.4 0 0 1 .139 1.626c0 2.434-.87 4.492-2.384 5.885h.002C11.978 15.292 10.158 16 8 16A8 8 0 1 1 8 0a7.7 7.7 0 0 1 5.352 2.082l-2.284 2.284A4.35 4.35 0 0 0 8 3.166c-2.087 0-3.86 1.408-4.492 3.304a4.8 4.8 0 0 0 0 3.063h.003c.635 1.893 2.405 3.301 4.492 3.301 1.078 0 2.004-.276 2.722-.764h-.003a3.7 3.7 0 0 0 1.599-2.431H8v-3.08z" />
          </svg>
          <span className="ps-2">Signup with Google</span>
        </button>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-lightgreen border-top-0 justify-content-center mt-0 pt-0 pb-4">
        <div className="text-center text-white">
          Already have an account?{" "}
          <span
            onClick={() => {
              closeModal();
              setLoginModalOpen(true);
            }}
            className="text-orange fw-bold mouse-pointer"
          >
            Login
          </span>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default SignupModal;
